class PdpRecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();

    this.initialized = false;
    this.resizeObserver = null;

    this.updateArrows = this.updateArrows.bind(this);
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.init();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateArrows);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  init() {
    const queryString = this.getQueryString();

    if (!queryString) {
      this.finalizeSection();
      return;
    }

    fetch(this.dataset.url + queryString)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        const section = html.querySelector('pdp-recently-viewed-products');

        if (section && section.innerHTML.trim().length) {
          this.innerHTML = section.innerHTML;
        }

        this.finalizeSection();
      })
      .catch((error) => {
        this.finalizeSection();
        console.error(error);
      });
  }

  getQueryString() {
    const storageKey = 'beyours:recently-viewed';
    let items = [];

    try {
      items = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    }
    catch (error) {
      items = [];
    }

    if (!Array.isArray(items) || !items.length) {
      return '';
    }

    const currentProductId = parseInt(this.dataset.productId, 10);

    if (currentProductId && items.includes(currentProductId)) {
      items.splice(items.indexOf(currentProductId), 1);
    }

    return items
      .filter(Boolean)
      .slice(0, 12)
      .map((item) => `id:${item}`)
      .join(' OR ');
  }

  finalizeSection() {
    if (!this.prepareProductLists()) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.setupProductLists();
    this.setupArrows();
    window.requestAnimationFrame(this.updateArrows);
    window.setTimeout(this.updateArrows, 250);
  }

  prepareProductLists() {
    const minimumProducts = Math.max(parseInt(this.dataset.minimumProducts, 10) || 6, 1);
    const currentProductId = parseInt(this.dataset.productId, 10);
    let hasProducts = false;

    this.querySelectorAll('[data-pdp-recently-viewed-products-list]').forEach((productList) => {
      const recentItems = [];
      const fallbackItems = [];
      const seenProductIds = new Set();

      Array.from(productList.children).forEach((productItem) => {
        const productId = parseInt(productItem.dataset.productId, 10);

        if (!productId || seenProductIds.has(productId) || (currentProductId && productId === currentProductId)) {
          return;
        }

        seenProductIds.add(productId);

        if (productItem.hasAttribute('data-fallback-product')) {
          fallbackItems.push(productItem);
        } else {
          recentItems.push(productItem);
        }
      });

      const finalItems = [...recentItems];

      if (finalItems.length < minimumProducts) {
        fallbackItems.forEach((productItem) => {
          if (finalItems.length < minimumProducts) {
            finalItems.push(productItem);
          }
        });
      }

      productList.replaceChildren(...finalItems);
      hasProducts = hasProducts || finalItems.length > 0;
    });

    return hasProducts;
  }

  setupProductLists() {
    this.productLists = Array.from(this.querySelectorAll('[data-pdp-recently-viewed-products-list]'));

    this.productLists.forEach((productList) => {
      initCarouselScroll(productList, {
        draggingClass: 'pdp-recently-viewed-products__product-list--dragging',
        onScroll: this.updateArrows,
      });
    });
  }

  setupArrows() {
    this.previousButtons = Array.from(this.querySelectorAll('[data-pdp-recently-viewed-products-previous]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-pdp-recently-viewed-products-next]'));

    this.previousButtons.forEach((button) => {
      button.addEventListener('click', this.onArrowClick.bind(this, 'previous'));
    });

    this.nextButtons.forEach((button) => {
      button.addEventListener('click', this.onArrowClick.bind(this, 'next'));
    });

    window.addEventListener('resize', this.updateArrows);
    this.setupResizeObserver();
    this.updateArrows();
  }

  setupResizeObserver() {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateArrows();
    });

    this.resizeObserver.observe(this);
    this.productLists.forEach((productList) => {
      this.resizeObserver.observe(productList);
    });
  }

  onArrowClick(direction, event) {
    const slider = event.currentTarget.closest('.pdp-recently-viewed-products__slider');
    const productList = slider ? slider.querySelector('[data-pdp-recently-viewed-products-list]') : null;

    if (!productList) {
      return;
    }

    const item = productList.querySelector('.pdp-recently-viewed-products__product-item');
    const gap = parseFloat(window.getComputedStyle(productList).columnGap) || 0;
    const scrollAmount = item ? item.getBoundingClientRect().width + gap : productList.clientWidth;

    productList.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });

    window.setTimeout(this.updateArrows, 350);
  }

  getStaticColumnCount(productCount) {
    if (window.matchMedia('(max-width: 749px)').matches) {
      return Math.min(productCount, 2);
    }

    if (window.matchMedia('(max-width: 1199px)').matches) {
      return Math.min(productCount, 3);
    }

    return Math.min(productCount, 4);
  }

  updateArrows() {
    this.querySelectorAll('.pdp-recently-viewed-products__slider').forEach((slider) => {
      const productList = slider.querySelector('[data-pdp-recently-viewed-products-list]');
      const previousButton = slider.querySelector('[data-pdp-recently-viewed-products-previous]');
      const nextButton = slider.querySelector('[data-pdp-recently-viewed-products-next]');

      if (!productList) {
        return;
      }

      const productCount = productList.querySelectorAll('.pdp-recently-viewed-products__product-item').length;
      productList.classList.remove('pdp-recently-viewed-products__product-list--static');
      const maxScroll = Math.max(productList.scrollWidth - productList.clientWidth, 0);
      const isScrollable = maxScroll > 1;
      const shouldShowArrows = Boolean(previousButton && nextButton) && isScrollable;
      const staticColumnCount = Math.max(this.getStaticColumnCount(productCount), 1);

      if (!isScrollable) {
        productList.scrollLeft = 0;
      }

      productList.style.setProperty('--pdp-recently-viewed-products-static-columns', staticColumnCount);
      productList.classList.toggle('pdp-recently-viewed-products__product-list--static', !isScrollable);
      slider.classList.toggle('pdp-recently-viewed-products__slider--arrows-visible', shouldShowArrows);

      if (previousButton && nextButton) {
        previousButton.hidden = !shouldShowArrows;
        nextButton.hidden = !shouldShowArrows;
        previousButton.disabled = !shouldShowArrows || productList.scrollLeft <= 1;
        nextButton.disabled = !shouldShowArrows || productList.scrollLeft >= maxScroll - 1;
      }
    });
  }
}

if (!customElements.get('pdp-recently-viewed-products')) {
  customElements.define('pdp-recently-viewed-products', PdpRecentlyViewedProducts);
}
